/**
 * CollaboratorsPanel Component
 * Displays active collaborators in a flow editing session
 */

import { useState } from 'react'
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Paper,
  IconButton,
  Chip,
  CircularProgress,
  Tooltip,
  Collapse,
} from '@mui/material'
import PeopleIcon from '@mui/icons-material/People'
import CloseIcon from '@mui/icons-material/Close'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord'
import type { FlowParticipant } from '@services/flowHub.service'

interface CollaboratorsPanelProps {
  participants: FlowParticipant[]
  currentUserId?: string
  isConnected: boolean
  isConnecting: boolean
  onClose: () => void
}

/**
 * Format relative time (e.g., "2m ago", "1h ago")
 */
function formatRelativeTime(timestamp: string): string {
  const now = new Date()
  const then = new Date(timestamp)
  const diffMs = now.getTime() - then.getTime()
  const diffSeconds = Math.floor(diffMs / 1000)
  const diffMinutes = Math.floor(diffSeconds / 60)
  const diffHours = Math.floor(diffMinutes / 60)

  if (diffSeconds < 60) {
    return 'just now'
  } else if (diffMinutes < 60) {
    return `${diffMinutes}m ago`
  } else if (diffHours < 24) {
    return `${diffHours}h ago`
  } else {
    return `${Math.floor(diffHours / 24)}d ago`
  }
}

/**
 * Get initials from a name
 */
function getInitials(name: string): string {
  const parts = name.split(' ').filter(Boolean)
  if (parts.length === 0) return '?'
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase()
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase()
}

/**
 * Generate a consistent color based on user ID
 */
function getUserColor(userId: string): string {
  const colors = [
    '#1976d2', // blue
    '#388e3c', // green
    '#f57c00', // orange
    '#7b1fa2', // purple
    '#c2185b', // pink
    '#0097a7', // cyan
    '#5d4037', // brown
    '#455a64', // blue-grey
  ]

  let hash = 0
  for (let i = 0; i < userId.length; i++) {
    hash = userId.charCodeAt(i) + ((hash << 5) - hash)
  }

  return colors[Math.abs(hash) % colors.length]
}

export function CollaboratorsPanel({
  participants,
  currentUserId,
  isConnected,
  isConnecting,
  onClose,
}: CollaboratorsPanelProps) {
  const [isExpanded, setIsExpanded] = useState(true)

  // Filter out the current user and deduplicate by userId (server may send duplicates)
  const otherParticipants = (currentUserId
    ? participants.filter(p => p.userId !== currentUserId)
    : participants
  ).filter((participant, index, self) =>
    // Keep only the first occurrence of each userId
    self.findIndex(p => p.userId === participant.userId) === index
  )

  // Auto-hide when no other collaborators are online (and connected)
  if (isConnected && otherParticipants.length === 0) {
    return null
  }

  const getConnectionStatus = () => {
    if (isConnecting) {
      return { color: 'warning.main', label: 'Connecting...' }
    }
    if (isConnected) {
      return { color: 'success.main', label: 'Connected' }
    }
    return { color: 'error.main', label: 'Disconnected' }
  }

  const status = getConnectionStatus()

  return (
    <Paper
      sx={{
        position: 'absolute',
        bottom: 16,
        left: 16,
        width: 280,
        maxHeight: 350,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        zIndex: 1000,
      }}
      elevation={6}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 1.5,
          borderBottom: 1,
          borderColor: 'divider',
          bgcolor: 'primary.main',
          color: 'white',
          cursor: 'pointer',
        }}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Tooltip title={status.label}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              {isConnecting ? (
                <CircularProgress size={16} sx={{ color: 'white' }} />
              ) : (
                <FiberManualRecordIcon
                  sx={{
                    fontSize: 12,
                    color: status.color,
                    filter: isConnected ? 'drop-shadow(0 0 2px rgba(76,175,80,0.8))' : undefined,
                  }}
                />
              )}
            </Box>
          </Tooltip>
          <PeopleIcon fontSize="small" />
          <Typography variant="subtitle2" fontWeight={600}>
            Collaborators
          </Typography>
          <Chip
            size="small"
            label={otherParticipants.length}
            sx={{
              bgcolor: 'rgba(255,255,255,0.2)',
              color: 'white',
              fontWeight: 'bold',
              height: 20,
              minWidth: 24,
            }}
          />
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation()
              setIsExpanded(!isExpanded)
            }}
            sx={{ color: 'white' }}
          >
            {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
          </IconButton>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation()
              onClose()
            }}
            sx={{ color: 'white' }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      {/* Content */}
      <Collapse in={isExpanded}>
        <Box sx={{ overflow: 'auto', maxHeight: 280 }}>
          {otherParticipants.length === 0 ? (
            <Box sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                {isConnected ? 'No other collaborators' : 'Not connected'}
              </Typography>
            </Box>
          ) : (
            <List dense disablePadding>
              {otherParticipants.map((participant) => (
                <ListItem
                  key={participant.userId}
                  sx={{
                    py: 1,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    '&:last-child': { borderBottom: 0 },
                  }}
                >
                  <ListItemAvatar sx={{ minWidth: 44 }}>
                    <Avatar
                      sx={{
                        width: 32,
                        height: 32,
                        fontSize: 14,
                        bgcolor: getUserColor(participant.userId),
                      }}
                    >
                      {getInitials(participant.userName)}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={participant.userName}
                    secondary={`Joined ${formatRelativeTime(participant.joinedAt)}`}
                    primaryTypographyProps={{
                      variant: 'body2',
                      fontWeight: 500,
                      noWrap: true,
                    }}
                    secondaryTypographyProps={{
                      variant: 'caption',
                    }}
                  />
                </ListItem>
              ))}
            </List>
          )}
        </Box>
      </Collapse>
    </Paper>
  )
}

export default CollaboratorsPanel
